import clsx from "clsx";
import { type ComponentProps, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Breadcrumbs } from "@features/navigations/breadcrumbs";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { MediaBackground } from "@shared/ui/media-background";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";
import { Heading } from "@shared/ui/typography";
import { formatEventDate } from "@shared/utils/string";

import type { EventType } from "@/_pages/event/model";

import s from "./hero.module.scss";

export type CompProps = ComponentProps<"div"> & {
  events: EventType[];
  title: string;
};

export const Hero = (props: CompProps) => {
  const { events, title } = props;

  const $swiper = useRef<SwiperType | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className={clsx(s.root)}>
      <div className={s.front}>
        <div>
          <Breadcrumbs className={s.breadcrumbs} />
          <Heading level={5}>{title}</Heading>
        </div>
        {events.length > 1 && (
          <SwiperNavButtons
            swiperRef={$swiper}
            variant="light"
            buttonType="secondary"
            totalSlides={events.length}
            currentIndex={activeIndex}
            className={s.buttons}
          />
        )}
      </div>
      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        pagination={{
          el: `.${s.pagination}`,
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        className={s.slider}
        loop
        effect="fade"
        fadeEffect={{ crossFade: true }}
        onSlideChange={({ realIndex }) => setActiveIndex(realIndex)}
        onInit={(swiper) => {
          $swiper.current = swiper;
        }}
      >
        {(events ?? []).map((event, index) =>
          event ? (
            <SwiperSlide
              key={
                event?.documentId ?? event?.slug ?? `slide-event-${index + 1}`
              }
              className={s.slide}
            >
              {event?.image && (
                <MediaBackground
                  media={event.imageForSlider || event.image}
                  className={s.bgImage}
                />
              )}
              <div className={s.slideContent}>
                <Heading level={5} className={s.location}>
                  {event?.date && (
                    <span>
                      {formatEventDate(event.date, event.dateEnd)}
                      {", "}
                    </span>
                  )}
                  {event?.location && <span>{event.location}</span>}
                </Heading>
                {event?.title != null && event.title !== "" && (
                  <Heading level={3} tag="h1" className={s.title}>
                    {event.title}
                  </Heading>
                )}
                <ButtonWithIcon
                  iconName={event?.pageIcon?.icon?.name ?? "plus"}
                  iconFillColor={event?.pageIcon?.fillColor ?? "gold"}
                  iconStrokeColor={event?.pageIcon?.strokeColor ?? "bistre"}
                  iconSvgCode={event?.pageIcon?.uicon?.svg_code}
                  variant="primary-light"
                  className={s.button}
                  href={event?.slug ? `/events/${event.slug}` : undefined}
                >
                  Подробнее
                </ButtonWithIcon>
              </div>
            </SwiperSlide>
          ) : null,
        )}
        {(events?.length ?? 0) > 1 && <div className={s.pagination}></div>}
      </Swiper>
    </section>
  );
};

Hero.displayName = "Hero";
